import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { mocApi } from '../api/client';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { CaretDown, CaretRight, CheckCircle, HardHat, FloppyDisk, Plus, ShieldCheck, FilePdf } from '@phosphor-icons/react';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

const CHOICES = [['yes', 'Oui'], ['no', 'Non'], ['na', 'N/A']];
const addDays = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

export function SafetyInDesignTab({ mocId, userEmail, focusNc }) {
  const [tpl, setTpl] = useState(null);
  const [resp, setResp] = useState({});
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState({}); // itemId -> true once an action is created
  const [ncOnly, setNcOnly] = useState(!!focusNc);

  const load = useCallback(() => {
    mocApi.getSidTemplate().then((t) => {
      setTpl(t);
      if (t && t.sections && t.sections.length) {
        if (focusNc) { const o = {}; t.sections.forEach((s) => { o[s.id] = true; }); setOpen(o); }
        else setOpen({ [t.sections[0].id]: true });
      }
    });
    mocApi.getSidResponses(mocId).then((r) => setResp((r && r.responses) || {}));
  }, [mocId, focusNc]);

  useEffect(() => { load(); }, [load]);

  if (!tpl) return <div className="py-10 text-center text-sm text-muted-foreground">Chargement de la check-list…</div>;
  if (!tpl.sections || tpl.sections.length === 0) {
    return (
      <Card className="rounded-sm p-6 text-center" data-testid="sid-empty">
        <ShieldCheck size={34} className="mx-auto text-muted-foreground mb-2" />
        <p className="text-sm text-muted-foreground">Aucune section n'est activée. Un administrateur peut les activer dans <b>Administration → Safety In Design</b>.</p>
      </Card>
    );
  }

  const setItem = (itemId, patch) => setResp((s) => ({ ...s, [itemId]: { ...(s[itemId] || {}), ...patch } }));
  const counts = (() => {
    let total = 0; let answered = 0; let no = 0;
    tpl.sections.forEach((sec) => sec.items.forEach((it) => {
      total += 1; const r = resp[it.id];
      if (r && r.compliance) answered += 1;
      if (r && r.compliance === 'no') no += 1;
    }));
    return { total, answered, no };
  })();

  const save = async () => {
    setBusy(true);
    const res = await mocApi.saveSidResponses(mocId, resp);
    setBusy(false);
    if (res && res.ok !== false) toast.success('Safety In Design enregistré');
    else toast.error('Échec de l\'enregistrement');
  };

  const createAction = async (sec, it) => {
    const res = await mocApi.createAction({
      MOC_ID: mocId,
      Description: `[Safety In Design] ${sec.title} — ${it.label}` + (it.ref ? ` (${it.ref})` : ''),
      Responsable_ID: userEmail || '',
      Echeance: addDays(14),
      Origine: 'Safety_In_Design',
      Obligatoire_Avant_Demarrage: true,
    });
    if (res.ok) { toast.success('Action créée (voir l\'onglet Actions pour le responsable et le délai)'); setCreated((s) => ({ ...s, [it.id]: true })); }
    else toast.error(res.error || 'Échec de la création');
  };

  const genPdf = async () => {
    const lbl = (c) => (c === 'yes' ? 'Conforme' : c === 'no' ? 'Non conforme' : c === 'na' ? 'N/A' : 'Non évalué');
    const doc = new jsPDF();
    doc.setFontSize(15); doc.text('Safety In Design', 14, 16);
    doc.setFontSize(10); doc.text(`MOC : ${mocId}`, 14, 23);
    doc.text(`Édité le ${new Date().toLocaleDateString('fr-FR')} — ${counts.answered}/${counts.total} évalués, ${counts.no} non conforme(s)`, 14, 28);
    let y = 34;
    tpl.sections.forEach((sec) => {
      const body = sec.items.map((it) => { const r = resp[it.id] || {}; return [it.label, lbl(r.compliance), r.comment || '']; });
      autoTable(doc, {
        startY: y, head: [[sec.title, 'Conformité', 'Commentaire']], body,
        styles: { fontSize: 8, cellWidth: 'wrap', valign: 'top' },
        headStyles: { fillColor: [30, 64, 175] },
        columnStyles: { 0: { cellWidth: 95 }, 1: { cellWidth: 26 }, 2: { cellWidth: 'auto' } },
        margin: { left: 14, right: 14 },
      });
      y = doc.lastAutoTable.finalY + 6;
      if (y > 265) { doc.addPage(); y = 16; }
    });
    try {
      const actions = await mocApi.listActions(mocId);
      if (actions && actions.length) {
        doc.addPage(); doc.setFontSize(13); doc.text("Plan d'actions de la MOC", 14, 16);
        autoTable(doc, {
          startY: 22,
          head: [['Action', 'Responsable', 'Échéance', 'Statut', 'Origine']],
          body: actions.map((a) => [a.description || a.title || '', a.owner || a.Responsable_ID || '', a.due || a.Echeance || '', a.statusLabel || a.status || '', a.origin || a.Origine || '']),
          styles: { fontSize: 8, valign: 'top' }, headStyles: { fillColor: [30, 64, 175] }, margin: { left: 14, right: 14 },
        });
      }
    } catch (e) { /* ignore */ }
    doc.save(`SafetyInDesign_${mocId}.pdf`);
    toast.success('Rapport PDF généré');
  };

  return (
    <div className="space-y-4" data-testid="sid-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <HardHat size={18} weight="bold" className="text-primary" />
          <span data-testid="sid-progress">{counts.answered}/{counts.total} évalués · <span className="text-rose-600 font-semibold">{counts.no} non conforme(s)</span></span>
        </div>
        <div className="flex gap-2">
          <Button variant={ncOnly ? 'default' : 'outline'} size="sm" data-testid="sid-nc-toggle" onClick={() => setNcOnly((v) => !v)}>
            Non conformités{ncOnly ? ' ✓' : ''}
          </Button>
          <Button data-testid="sid-pdf" variant="outline" size="sm" onClick={genPdf}>
            <FilePdf size={15} weight="bold" className="mr-1.5" />Rapport PDF
          </Button>
          <Button data-testid="sid-save" size="sm" disabled={busy} onClick={save}>
            <FloppyDisk size={15} weight="bold" className="mr-1.5" />Enregistrer
          </Button>
        </div>
      </div>

      {tpl.sections.map((sec) => {
        const isOpen = !!open[sec.id];
        const secNo = sec.items.filter((it) => (resp[it.id] || {}).compliance === 'no').length;
        const items = ncOnly ? sec.items.filter((it) => (resp[it.id] || {}).compliance === 'no') : sec.items;
        if (ncOnly && items.length === 0) return null;
        return (
          <Card key={sec.id} className="rounded-sm overflow-hidden" data-testid={`sid-section-${sec.id}`}>
            <button type="button" onClick={() => setOpen((s) => ({ ...s, [sec.id]: !s[sec.id] }))}
              data-testid={`sid-section-toggle-${sec.id}`}
              className="w-full flex items-center justify-between gap-3 px-4 py-3 bg-muted/50 hover:bg-muted text-left">
              <span className="flex items-center gap-2 font-display font-bold text-sm">
                {isOpen ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
                {sec.title}
              </span>
              <span className="text-xs text-muted-foreground">
                {secNo > 0 && <span className="text-rose-600 font-semibold mr-2">{secNo} non conforme(s)</span>}
                {sec.items.length} point(s)
              </span>
            </button>
            {isOpen && (
              <div className="divide-y divide-border">
                {items.map((it) => {
                  const r = resp[it.id] || {};
                  return (
                    <div key={it.id} className="px-4 py-3" data-testid={`sid-item-${it.id}`}>
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                        <div className="flex-1">
                          <div className="text-sm font-medium">{it.label}</div>
                          {it.ref && <div className="text-xs text-muted-foreground mt-0.5">{it.ref}</div>}
                        </div>
                        <div className="flex gap-1 shrink-0">
                          {CHOICES.map(([val, lbl]) => (
                            <button key={val} type="button" onClick={() => setItem(it.id, { compliance: val })}
                              data-testid={`sid-choice-${it.id}-${val}`}
                              className={`text-xs px-2.5 py-1 rounded border transition-colors ${r.compliance === val
                                ? (val === 'no' ? 'bg-rose-600 text-white border-rose-600' : val === 'yes' ? 'bg-emerald-600 text-white border-emerald-600' : 'bg-muted-foreground text-white border-muted-foreground')
                                : 'border-border text-muted-foreground hover:bg-muted'}`}>
                              {lbl}
                            </button>
                          ))}
                        </div>
                      </div>
                      {r.compliance === 'no' && (
                        <div className="mt-2 space-y-2">
                          <Textarea data-testid={`sid-comment-${it.id}`} placeholder="Commentaire / écart constaté…"
                            value={r.comment || ''} onChange={(e) => setItem(it.id, { comment: e.target.value })}
                            className="min-h-[52px] text-sm" />
                          <Button variant="outline" size="sm" data-testid={`sid-create-action-${it.id}`}
                            disabled={created[it.id]} onClick={() => createAction(sec, it)}>
                            {created[it.id] ? <><CheckCircle size={14} weight="fill" className="mr-1.5 text-emerald-600" />Action créée</>
                              : <><Plus size={14} weight="bold" className="mr-1.5" />Créer une action</>}
                          </Button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );
}
